import { Button, PublicPageHead, PublicShareLayout, usePublicShareRouteParam } from '@yeshuman/sdk'
import { useEffect, useState } from 'react'


import { SlidePreview } from './slide-preview'
import { fetchPublicSlideDeck, type PublicSlideDeck } from './use-slides'

export function PublicSlidePage() {
  const slug = usePublicShareRouteParam('/s/:slug', 'slug')
  const [deck, setDeck] = useState<PublicSlideDeck | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [index, setIndex] = useState(0)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!slug) {
        if (!cancelled) {
          setLoading(false)
          setError('Deck not found')
        }
        return
      }
      setLoading(true)
      setError(null)
      try {
        const data = await fetchPublicSlideDeck(slug)
        if (!cancelled) {
          setDeck(data)
          setIndex(0)
        }
      } catch {
        if (!cancelled) {
          setDeck(null)
          setError('Deck not found')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slug])

  const slide = deck?.slides[index]

  return (
    <PublicShareLayout>
      <PublicPageHead title={deck?.title || 'Slides'} />
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error || !deck || !slide ? (
        <p className="text-sm text-muted-foreground">This deck is not available.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-medium text-foreground">{deck.title.trim() || 'Untitled deck'}</h1>
            <p className="text-sm text-muted-foreground">
              Slide {index + 1} of {deck.slides.length}
            </p>
          </div>
          <SlidePreview
            html={slide.html}
            css={slide.css}
            title={deck.title || 'Slide'}
            className="min-h-[28rem] w-full rounded-md border border-border bg-background"
          />
          {deck.slides.length > 1 ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={index === 0}
                onClick={() => setIndex((value) => Math.max(0, value - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={index >= deck.slides.length - 1}
                onClick={() => setIndex((value) => Math.min(deck.slides.length - 1, value + 1))}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </PublicShareLayout>
  )
}
